import { useEffect, useRef, useState, type ComponentType } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'
import { CalendarClock, LogOut, Radio, Trophy, Tv, Users, Vote } from 'lucide-react'

import { LanguageSwitcher } from '@/design-system/LanguageSwitcher'
import { BottomBarSpacer, Container } from '@/design-system/primitives'
import { usePublicEvent } from '@/features/event/api'
import { formatTime } from '@/lib/datetime'
import { cn } from '@/lib/cn'
import { NoAccess, StaffGate, isForbidden } from '@/lib/staffGate'
import type { StaffSession } from '@/lib/staffSession'

import { useConductor, useStaffPermissions } from '../api'
import { CakeCard } from '../cockpit/CakeCard'
import { ContestTab } from '../cockpit/ContestTab'
import { JuryTab } from '../cockpit/JuryTab'
import { LiveTab } from '../cockpit/LiveTab'
import { ScreenTab } from '../cockpit/ScreenTab'
import { text } from '../cockpit/shared'
import { VotesTab } from '../cockpit/VotesTab'
import { liveTiming, type LiveTiming } from '../plan'
import { PlanEditor } from '../PlanEditor'

/** En deçà, un écart sur le déroulé n'est pas un retard. */
const DELAY_TOLERANCE = 3

type TabId = 'live' | 'plan' | 'votes' | 'contest' | 'jury' | 'screen'

const TABS: { id: TabId; icon: ComponentType<{ className?: string }> }[] = [
  { id: 'live', icon: Radio },
  { id: 'plan', icon: CalendarClock },
  { id: 'votes', icon: Vote },
  { id: 'contest', icon: Trophy },
  { id: 'jury', icon: Users },
  { id: 'screen', icon: Tv },
]

/**
 * Le cockpit du maître de cérémonie — `/ops/deroule`.
 *
 * Tout ce qu'il pilote, sur un seul écran, sans jamais se reconnecter
 * ailleurs : le déroulé live, son programme, les votes, le concours et ses
 * tours (classement privé, départage, proclamation, publication), le jury,
 * et ce que l'écran de la salle affiche. Le service, la caisse et le reste
 * ne sont pas les siens : ils n'apparaissent pas ici.
 *
 * L'en-tête reste collé en haut — l'étape en cours, la suivante, le retard
 * sur le déroulé — parce que c'est la question qu'il se pose entre deux
 * phrases au micro, quel que soit l'onglet ouvert.
 */
export function ConductorPage() {
  const { t } = useTranslation()
  return (
    <StaffGate title={t('conductor.overline')}>
      {(session, signOut) => <Cockpit session={session} onSignOut={signOut} />}
    </StaffGate>
  )
}

/** L'heure courante, relue toutes les quinze secondes : assez pour des minutes. */
function useNow(): number {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 15_000)
    return () => window.clearInterval(timer)
  }, [])
  return now
}

function Cockpit({ session, onSignOut }: { session: StaffSession; onSignOut: () => void }) {
  const { t } = useTranslation()
  const eventQuery = usePublicEvent()
  const eventId = eventQuery.data?.id
  const timezone = eventQuery.data?.timezone ?? 'Europe/Berlin'
  const conductor = useConductor(eventId, true)
  // Les droits ne servent qu'à MASQUER ce qui serait refusé : chaque appel
  // reste vérifié par le serveur.
  const can = useStaffPermissions(eventId, true)
  const now = useNow()
  const [params, setParams] = useSearchParams()
  // La barre d'onglets du bas : sa hauteur réelle (encoche comprise) est
  // réservée sous la page, sinon la dernière étape finit dessous.
  const tabBar = useRef<HTMLElement>(null)

  useEffect(() => {
    document.title = t('conductor.overline')
  }, [t])

  // Un onglet n'apparaît que si le compte peut s'en servir : sinon il
  // n'ouvrirait que des refus.
  const visible: Record<TabId, boolean> = {
    live: true,
    plan: can('program.item.update') || can('program.item.create'),
    votes: can('voting.category.read'),
    contest: can('competition.round.read'),
    jury:
      can('competition.jury.manage') ||
      can('competition.category.update') ||
      can('voting.result.read'),
    screen: can('program.item.read'),
  }
  const tabs = TABS.filter((tab) => visible[tab.id])
  const requested = params.get('onglet') as TabId | null
  // L'onglet vit dans l'adresse : un rechargement (ou un téléphone qui se
  // réveille) rouvre là où l'on était.
  const tab: TabId = requested && visible[requested] ? requested : 'live'
  const choose = (id: TabId) => {
    setParams(
      (current) => {
        const next = new URLSearchParams(current)
        if (id === 'live') next.delete('onglet')
        else next.set('onglet', id)
        return next
      },
      { replace: true },
    )
    window.scrollTo({ top: 0 })
  }

  if (isForbidden(conductor.error)) {
    return <NoAccess title={t('conductor.overline')} onSignOut={onSignOut} />
  }

  const items = conductor.data ?? []
  const timing = liveTiming(items, now)

  return (
    <div className="min-h-dvh bg-bg-base">
      <header className="sticky top-0 z-30 border-b border-line-subtle bg-bg-base backdrop-blur">
        <Container className="px-4 py-3 sm:px-6">
          <div className="flex items-center justify-between gap-3">
            <p className="overline min-w-0 flex-1 break-words text-champagne-400">
              {t('conductor.overline')}
              {eventQuery.data?.name ? ` · ${eventQuery.data.name}` : ''}
            </p>
            <LanguageSwitcher />
            <button
              type="button"
              onClick={onSignOut}
              title={session.user.email}
              aria-label={t('checkin.logout')}
              className="inline-flex h-10 shrink-0 items-center gap-2 rounded-full border border-line-subtle px-3 text-caption text-content-secondary hover:text-content-primary"
            >
              <LogOut className="h-4 w-4" aria-hidden />
              <span className="hidden sm:inline">{t('checkin.logout')}</span>
            </button>
          </div>
          <NowNext timing={timing} timezone={timezone} />

          <nav
            aria-label={t('conductor.cockpit.tabs')}
            className="mt-3 hidden gap-1 md:flex"
          >
            {tabs.map(({ id, icon: Icon }) => (
              <button
                key={id}
                type="button"
                aria-current={tab === id ? 'page' : undefined}
                onClick={() => choose(id)}
                className={cn(
                  'inline-flex h-10 items-center gap-2 rounded-full px-4 text-sm transition',
                  tab === id
                    ? 'bg-champagne-400/15 text-champagne-300'
                    : 'text-content-secondary hover:text-content-primary',
                )}
              >
                <Icon className="h-4 w-4" aria-hidden />
                {t(`conductor.cockpit.tab.${id}`)}
              </button>
            ))}
          </nav>
        </Container>
      </header>

      <Container className="px-4 pb-6 pt-6 sm:px-6 md:pb-12">
        {tab === 'live' ? (
          <LiveTab
            eventId={eventId}
            items={items}
            timing={timing}
            timezone={timezone}
            isPending={conductor.isPending}
            can={can}
          />
        ) : null}
        {tab === 'plan' ? (
          <div className="space-y-4">
            <CakeCard eventId={eventId} items={items} can={can} />
            <PlanEditor eventId={eventId} items={items} can={can} />
          </div>
        ) : null}
        {tab === 'votes' ? <VotesTab eventId={eventId} can={can} /> : null}
        {tab === 'contest' ? <ContestTab eventId={eventId} can={can} /> : null}
        {tab === 'jury' ? <JuryTab eventId={eventId} can={can} /> : null}
        {tab === 'screen' ? <ScreenTab eventId={eventId} timezone={timezone} can={can} /> : null}
      </Container>

      {tabs.length > 1 ? <BottomBarSpacer barRef={tabBar} className="md:hidden" /> : null}

      {/* Sur téléphone, les onglets sont sous le pouce, en bas de l'écran. */}
      {tabs.length > 1 ? (
        <nav
          ref={tabBar}
          aria-label={t('conductor.cockpit.tabs')}
          className="fixed inset-x-0 bottom-0 z-30 border-t border-line-subtle bg-bg-base pb-[env(safe-area-inset-bottom)] backdrop-blur md:hidden"
        >
          <div
            className="grid"
            style={{ gridTemplateColumns: `repeat(${tabs.length}, minmax(0, 1fr))` }}
          >
            {tabs.map(({ id, icon: Icon }) => (
              <button
                key={id}
                type="button"
                aria-current={tab === id ? 'page' : undefined}
                onClick={() => choose(id)}
                className={cn(
                  'flex min-h-14 min-w-0 flex-col items-center justify-center gap-1 px-0.5 py-2 text-[10px] leading-tight min-[360px]:text-[11px]',
                  tab === id ? 'text-champagne-300' : 'text-content-muted',
                )}
              >
                <Icon className="h-5 w-5 shrink-0" aria-hidden />
                <span className="max-w-full truncate">{t(`conductor.cockpit.tab.${id}`)}</span>
              </button>
            ))}
          </div>
        </nav>
      ) : null}
    </div>
  )
}

/** Maintenant, ensuite, et le retard : ce que l'en-tête répète sur chaque onglet. */
function NowNext({ timing, timezone }: { timing: LiveTiming; timezone: string }) {
  const { t, i18n } = useTranslation()
  const locale = i18n.language
  const { running, next, elapsed, planned, delay } = timing
  const overrun = elapsed !== null && planned !== null && elapsed > planned

  return (
    <div className="mt-2 space-y-1">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <p className="flex flex-wrap items-baseline gap-x-2 text-caption text-content-muted">
            <span>{t('screen.now')}</span>
            {running && elapsed !== null ? (
              <span
                className={cn(
                  'whitespace-nowrap font-mono tabular-nums',
                  overrun ? 'text-state-warning' : 'text-content-muted',
                )}
              >
                {planned !== null
                  ? t('conductor.cockpit.header.elapsedOf', { elapsed, planned })
                  : t('conductor.cockpit.header.elapsed', { elapsed })}
              </span>
            ) : null}
          </p>
          <p className="break-words font-display text-content-primary">
            {running ? text(running.title_i18n, locale) : t('conductor.nothingRunning')}
          </p>
        </div>
        <DelayBadge delay={delay} />
      </div>
      {next ? (
        <p className="break-words text-caption text-content-secondary">
          <span className="text-content-muted">{t('screen.next')} · </span>
          {next.starts_at ? (
            <span className="font-mono tabular-nums text-champagne-300">
              {formatTime(next.starts_at, locale, timezone)}{' '}
            </span>
          ) : null}
          {text(next.title_i18n, locale)}
        </p>
      ) : null}
    </div>
  )
}

/**
 * Au-delà de douze heures, l'écart ne parle plus de la soirée : c'est une
 * étape lancée pour essayer, des jours avant. On ne l'affiche pas.
 */
const DELAY_MEANINGFUL = 12 * 60

function DelayBadge({ delay }: { delay: number | null }) {
  const { t } = useTranslation()
  if (delay === null || Math.abs(delay) > DELAY_MEANINGFUL) return null
  const [label, tone] =
    delay >= DELAY_TOLERANCE
      ? [t('conductor.cockpit.header.late', { minutes: delay }), 'border-state-warning/50 text-state-warning']
      : delay <= -DELAY_TOLERANCE
        ? [t('conductor.cockpit.header.ahead', { minutes: -delay }), 'border-champagne-400/40 text-champagne-300']
        : [t('conductor.cockpit.header.onTime'), 'border-state-success/40 text-state-success']
  return (
    <span
      className={cn(
        'shrink-0 whitespace-nowrap rounded-full border px-2.5 py-0.5 text-caption tabular-nums',
        tone,
      )}
    >
      {label}
    </span>
  )
}
